// src/components/layout/Sidebar.jsx

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom'; // Import Link and useLocation
import { useAppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { getNavSections } from '../../data/navData';
import { version, releaseNotes } from '../../config';
import ReleaseNotesModal from '../common/ReleaseNotesModal';

/**
 * Sidebar component displaying navigation links.
 * 
 * IMPORTANT: This component uses `react-router-dom` for navigation.
 * - It uses the `<Link>` component to navigate between routes defined in `Layout.jsx`.
 * - It uses the `useLocation` hook to determine the currently active route for highlighting.
 * - The `navData.js` file must contain the correct `path` property for each navigation item.
 * 
 * Do NOT revert to using internal state management (like `activePage` in context) for navigation,
 * as this will break the routing integration.
 */
const Sidebar = () => {
  // Component state
  const [isModalOpen, setModalOpen] = useState(false);
  const [isRecentRelease, setIsRecentRelease] = useState(false);

  useEffect(() => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const latestReleaseDate = new Date(releaseNotes[0].date);
    
    if (latestReleaseDate > sevenDaysAgo) {
      setIsRecentRelease(true);
    }
  }, []);

  // Get resetSportsState from context
  const { sideMenuWidth, resetSportsState, t } = useAppContext(); 
  const { hasPermission, user } = useAuth();
  const location = useLocation(); // Get current location

  const navSections = getNavSections(t);

  // Function to determine if a path is active. Requires an exact match.
  const isActive = (path) => {
    // Active only if the current pathname exactly matches the link's path
    return location.pathname === path;
  };

  // Separate admin, Telus, Carrier Management, and non-admin sections
  const adminSection = navSections.find(section => section.title === 'ADMIN');
  const telusSection = navSections.find(section => section.title === 'TELUS');
  const carrierManagementSection = navSections.find(section => section.title === 'CARRIER MANAGEMENT');
  const nonAdminAndNonTelusSections = navSections.filter(section =>
    section.title !== 'ADMIN' &&
    section.title !== 'TELUS' &&
    section.title !== 'CARRIER MANAGEMENT'
  );

  return (
    <div 
      className="pt-10 bg-white border-r border-gray-200 flex flex-col h-full" 
      style={{ width: `${sideMenuWidth}px` }}
    >
      {/* Main navigation sections */}
      <div className="flex-1 overflow-y-auto">
        {nonAdminAndNonTelusSections.map((section, idx) => (
          <div key={idx} className="mb-2">
            {section.title && (
              <div className="px-4 py-2 text-xs font-semibold text-gray-500">
                {section.title}
              </div>
            )}
            {section.items.map(item => (
              <Link
                key={item.id}
                to={item.path} // Use the path from navData
                className={`w-full text-left px-4 py-1.5 flex items-center ${
                  isActive(item.path) // Use location.pathname for active state
                    ? 'bg-gray-100 border-l-2 border-blue-500 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                // Add onClick specifically for the sports link to reset state
                onClick={item.path === '/sports' ? resetSportsState : undefined}
              >
                <span className="mr-2 text-xs">{item.icon}</span>
                <span className="text-xs">{item.label}</span>
              </Link>
            ))}
            {idx < nonAdminAndNonTelusSections.length - 1 && section.title && (
              <div className="mx-4 my-2 border-b border-gray-200"></div>
            )}
          </div>
        ))}

        {/* Telus section - conditionally rendered */}
        {telusSection && hasPermission('telus.read') && (
          <div className="mb-2">
            <div className="mx-4 my-2 border-b border-gray-200"></div> {/* Separator */}
            <div className="px-4 py-2 text-xs font-semibold text-gray-500">
              {telusSection.title}
            </div>
            {telusSection.items.map(item => (
              <Link
                key={item.id}
                to={item.path}
                className={`w-full text-left px-4 py-1.5 flex items-center ${
                  isActive(item.path)
                    ? 'bg-gray-100 border-l-2 border-blue-500 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="mr-2 text-xs">{item.icon}</span>
                <span className="text-xs">{item.label}</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="mt-auto pt-2 pb-2">
        <div
            className={`px-4 py-1.5 text-xs cursor-pointer ${isRecentRelease ? 'text-blue-500 font-bold' : 'text-gray-600'} hover:bg-gray-50`}
            onClick={() => setModalOpen(true)}
        >
            Version: {version}
        </div>
      </div>

      {/* Management sections grouped together at bottom */}
      {(carrierManagementSection && hasPermission('manager.read')) || (adminSection && hasPermission('system.admin')) ? (
        <div className="border-t border-gray-200 pt-2 pb-2">
          {/* Carrier Management section */}
          {carrierManagementSection && hasPermission('manager.read') && (
            <>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500">
                {carrierManagementSection.title}
              </div>
              {carrierManagementSection.items.map(item => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`w-full text-left px-4 py-1.5 flex items-center ${
                    isActive(item.path)
                      ? 'bg-gray-100 border-l-2 border-blue-500 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="mr-2 text-xs">{item.icon}</span>
                  <span className="text-xs">{item.label}</span>
                </Link>
              ))}
              {/* Add separator if both sections are visible */}
              {adminSection && hasPermission('system.admin') && (
                <div className="mx-4 my-2 border-b border-gray-200"></div>
              )}
            </>
          )}

          {/* Admin section */}
          {adminSection && hasPermission('system.admin') && (
            <>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500">
                {adminSection.title}
              </div>
              {adminSection.items.map(item => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`w-full text-left px-4 py-1.5 flex items-center ${
                    isActive(item.path)
                      ? 'bg-gray-100 border-l-2 border-blue-500 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="mr-2 text-xs">{item.icon}</span>
                  <span className="text-xs">{item.label}</span>
                </Link>
              ))}
            </>
          )}
        </div>
      ) : null}
      <ReleaseNotesModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Sidebar;
